import React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";
/*
  The datepicker's own stylesheet, vendored in the Calendar folder and shared
  rather than copied a second time -- it is react-datepicker's, not ours.
*/
import "../Calendar/styles/DatePickerStyle.css";
import fieldStyles from "./styles/ReportDateField.module.css";

registerLocale("fr", fr);

/*
  The Wealth Report's custom-date field.

  It replaces <input type="date">, which could not be made to read MM/DD/YYYY in
  English: a native date input is drawn by the browser in the BROWSER's locale,
  and neither `lang` on the input nor `lang` on an ancestor changes it -- both
  were tried, both still rendered DD/MM. There is no attribute or CSS for it.

  So the control is ours now, and it wears whatever class the native one did at
  each call site -- same box, same border, same radius. What changed is that the
  text in it is formatted by us.

  Shared: the Wealth Report's date filter, the debt modal, the Add / Edit Asset
  modal and the valuation modal. It still lives in the report's folder, which is
  where it was written; that is worth moving the day a fifth caller appears.
*/

/*
  The stored shape, and the one the rest of the report reads: YYYY-MM-DD. Only
  the display differs by language.
*/
export const DATE_FORMATS = {
  en: "MM/dd/yyyy",
  fr: "dd/MM/yyyy",
};

/*
  Shown while the field is empty.

  Each language names its own parts: a French reader is told jour, mois,
  année, not day, month, year -- "dd/mm/yyyy" is an English hint wearing a
  French order, and it tells someone who does not read English nothing about
  what goes in the box. Uppercase because that is how French forms write it.

  English stays lower case, matching a native date input.
*/
export const DATE_PLACEHOLDERS = {
  en: "mm/dd/yyyy",
  fr: "JJ/MM/AAAA",
};

/*
  "YYYY-MM-DD" to a Date at LOCAL midnight.

  new Date("2026-09-05") is parsed as UTC, which in any negative offset is the
  4th once it is shown -- the date would read one day earlier than the one that
  was picked. Building it from parts keeps it local, which is what a calendar
  date means here: there is no time of day in it to convert.
*/
export const toDate = (value) => {
  const parts = String(value || "").match(/\d+/g);
  if (!parts || parts.length < 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

/*
  And back again, from the local parts for the same reason -- toISOString()
  converts to UTC and would hand back the previous day for anyone west of
  Greenwich.
*/
export const toValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/*
  `minValue` and `maxValue` bound what can be picked. They take the same
  "YYYY-MM-DD" string `value` does, rather than Date objects, so a caller
  holding one end of a range can hand the other straight over without
  converting -- the parsing is already here, and doing it in one place is what
  keeps the local-midnight rule above from being re-derived per caller.

  Empty or absent means unbounded, which is what an open-ended end of a range
  resolves to.
*/
export default function ReportDateField({
  value,
  onChange,
  french = false,
  className,
  calendarClassName,
  autoFocus = false,
  disabled = false,
  minValue,
  maxValue,
  ariaLabel,
}) {
  const key = french ? "fr" : "en";
  return (
    <DatePicker
      selected={toDate(value)}
      onChange={(date) => onChange(toValue(date))}
      dateFormat={DATE_FORMATS[key]}
      placeholderText={DATE_PLACEHOLDERS[key]}
      locale={french ? "fr" : undefined}
      className={className}
      /*
        The field's own popup class always applies -- that is what makes the
        chosen day blue everywhere -- and a caller may add to it.
      */
      calendarClassName={`${fieldStyles.calendar} ${calendarClassName || ""}`}
      autoFocus={autoFocus}
      disabled={disabled}
      /*
        Out-of-range days are greyed and refuse the click, and typing one is
        rejected too -- react-datepicker applies these to both paths.
      */
      minDate={toDate(minValue) || undefined}
      maxDate={toDate(maxValue) || undefined}
      /*
        Positioned against the viewport rather than the nearest positioned box.

        Three of the four callers sit inside a modal, and a modal here is
        `max-height: 80vh; overflow-y: auto`. A popup laid out inside that box
        is clipped by it, and the payoff date is the last field in the debt
        modal -- its calendar opens entirely below the fold and would simply not
        be visible. Fixed positioning leaves the scroll box; where nothing is
        clipping, it lands in exactly the same place as before.
      */
      popperProps={{ strategy: "fixed" }}
      /*
        Typing is allowed -- the native input allowed it too -- and the format
        above is what it is parsed against, so a French user types 05/09 and an
        English one types 09/05 and both mean the same day.
      */
      showPopperArrow={false}
      popperPlacement="bottom-start"
      /*
        The accessible name goes on through a custom input, because
        react-datepicker has no prop for it: `aria-label` handed to <DatePicker>
        is dropped on the floor, and the field went out with no name at all.
        Value, class, placeholder and the handlers are all cloned onto this
        element by the datepicker -- the only thing it contributes is the name.
      */
      customInput={<input aria-label={ariaLabel} />}
    />
  );
}
